'use client'

import { useMemo } from 'react'
import Image from 'next/image'
import cn from 'classnames'

import Calculator from 'components/ui/calculator'
import { useAppSelector } from 'store/hooks'
import { getCurrencies } from 'store/reducers/currencies'

import { useTranslation } from 'utils/use-translation'

import { useFloatingTrustpilot } from 'utils/use-trustpilot-widget'

import PartnerChangenow from './icons/partner-changenow.svg'
import PartnerGuarda from './icons/partner-guarda.svg'
import nowWalletLogo from './icons/nowwallet-logo.svg'
import nowWalletLogoMobile from './icons/nowwallet-logo-mobile.svg'

import styles from './styles.module.scss'

declare global {
  interface Window {
    Trustpilot?: {
      loadFromElement: (element: Element) => void
    }
  }
}

export default function MainBlock() {
  const { t, locale } = useTranslation()
  const currencies = useAppSelector(getCurrencies)

  useFloatingTrustpilot('trustpilot-target')

  const currenciesCount = useMemo(() => {
    const count = currencies?.filter((el) => el.is_loan_deposit_enabled || el.is_loan_receive_enabled).length

    return count ? `${count}+` : ''
  }, [currencies])

  return (
    <section className={styles.container}>
      <div className={styles.mainBlock}>
        <div className={cn('container', styles.mainBlockWrapper)}>
          <div className={styles.greeting}>
            <h1 className={styles.mainTitle}>{t('crypto_loans')}</h1>
            <h2 className={styles.titlePage}>
              {locale === 'en' ? (
                <>
                  <span className={styles.firstWord}>Get</span> Instant Funds <br /> Against Your Crypto
                </>
              ) : (
                <>
                  <span className={styles.firstWord}>{t('get')}</span> {t('get_instant_funds_against_your_crypto')}
                </>
              )}
            </h2>
          </div>
          <div className={styles.additionalInfo}>
            <div className={styles.subtitleText}>
              <p>{t('access_extra_funds_without_selling_crypto')}</p>
              <p>{t('grow_portfolio')}</p>
            </div>
            <div className={styles.advantages}>
              <div className={styles.advantagesItem}>
                <p className={styles.advantagesMain}>{t('safe')}</p>
                <p className={styles.advantagesText}>
                  <span className={styles.nowrap}>{t('top_tier_security')},</span>
                  <span>{t('cold_wallet_storage')}</span>
                </p>
              </div>
              <div className={styles.advantagesItem}>
                <p className={styles.advantagesMain}>{t('easy')}</p>
                <p className={styles.advantagesText}>
                  <span>{t('no_credit_check')}</span>
                </p>
              </div>
              <div className={styles.advantagesItem}>
                <p className={styles.advantagesMain}>{t('start')}</p>
                <p className={styles.advantagesText}>{t('from')} $100</p>
              </div>
              <div className={styles.advantagesItem}>
                <p className={styles.advantagesMain}>{currenciesCount}</p>
                <p className={styles.advantagesText}>{t('currencies')}</p>
              </div>
            </div>
            <div className={styles.partners}>
              <div className={styles.partnersBoxInner}>
                <span className={cn(styles.partnersBoxTitle, styles.desktopOnly)}>{t('our_partners_capital')}</span>
                <div className={styles.partnersBoxItems}>
                  <a
                    href="https://changenow.io/"
                    rel="noopener noreferrer nofollow"
                    target="_blank"
                    className={styles.partner}
                  >
                    <Image src={PartnerChangenow} alt="changeNow" width={58} height={33} loading="lazy" />
                  </a>
                  <a
                    href="https://guarda.com/"
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className={cn(styles.guarda, styles.partner)}
                  >
                    <Image src={PartnerGuarda} alt="Guarda Wallet" width={65} height={50} loading="lazy" />
                  </a>
                  <a
                    href="https://walletnow.app/"
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className={cn(styles.wallet, styles.partner, styles.mobileOnly)}
                  >
                    <Image
                      src={nowWalletLogoMobile}
                      alt="NOW wallet"
                      width={30}
                      height={32}
                      layout={'fixed'}
                      loading="lazy"
                    />
                  </a>
                </div>
              </div>
              <div className={cn(styles.partnersBoxInner, styles.desktopOnly)}>
                <span className={cn(styles.partnersBoxTitle, styles.desktopOnly)}>{t('recommended_wallet')}</span>
                <div className={styles.partnersBoxItems}>
                  <a
                    href="https://walletnow.app/"
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className={cn(styles.wallet, styles.partner, styles.desktopOnly)}
                  >
                    <Image
                      src={nowWalletLogo}
                      alt="NOW wallet"
                      width={110}
                      height={50}
                      layout={'fixed'}
                      loading="lazy"
                    />
                  </a>
                </div>
              </div>
              <div className={styles.partnersBoxInner}>
                <span className={cn(styles.partnersBoxTitle, styles.desktopOnly)}>{t('rated_excellent')}</span>
                <div id="trustpilot-target" />
              </div>
            </div>
          </div>
          <div className={styles.calculator}>
            <Calculator />
          </div>
        </div>
      </div>
    </section>
  )
}
